import React, { useState } from 'react';
import styled from 'styled-components';
import { api } from '../api';
import toast from 'react-hot-toast';
import { Brain, Copy, CheckCircle, Loader } from 'lucide-react';
import { authHeader } from '../auth';
import AccessGateModal from '../components/AccessGateModal';
import useAccessGate from '../hooks/useAccessGate';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  color: #1f2937;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(15, 23, 42, 0.35);
  margin-bottom: 1.5rem;
`;

const Card = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 10px 25px rgba(0,0,0,0.03);
  margin-bottom: 1rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 110px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  resize: vertical;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
`;

const SectionTitle = styled.h3`
  font-weight: 700;
  color: #1f2937;
  margin: 0.5rem 0 0.75rem;
`;

const List = styled.ul`
  margin: 0.5rem 0 0 1rem;
`;

const CareerCoach = () => {
  const { gate, requirePremium, closeGate } = useAccessGate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    targetRole: '',
    sector: '',
    level: '',
    employment: '',
    summary: '',
    skills: '',
    education: '',
    certifications: '',
    projects: '',
    linkedin: ''
  });
  const [result, setResult] = useState(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const toList = (s) => s.split(',').map(t => t.trim()).filter(Boolean);

  const advise = async () => {
    const allowed = await requirePremium();
    if (!allowed) return;
    if (!form.targetRole && !form.sector) {
      toast.error('Hedef pozisyon veya sektor gerekli');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        targetRole: form.targetRole,
        sector: form.sector,
        level: form.level || undefined,
        employment: form.employment || undefined,
        summary: form.summary || undefined,
        skills: toList(form.skills),
        education: toList(form.education).map(x => ({ school: x })),
        certifications: toList(form.certifications),
        projects: toList(form.projects),
        linkedin: form.linkedin || undefined
      };
      const { data } = await api.post('/api/coach/advise', payload, {
        headers: authHeader()
      });
      setResult(data);
      toast.success('Tavsiyeler hazır');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Bir hata olustu');
    } finally {
      setLoading(false);
    }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Kopyalandi'); };

  return (
    <>
      <Container>
        <Title>Kariyer Koclugu</Title>
        <Subtitle>Profilini gir, AI aksiyon planını oluştursun.</Subtitle>

        <Card>
          <Grid>
            <div>
              <Label>Hedef Pozisyon</Label>
              <Input
                name="targetRole"
                value={form.targetRole}
                onChange={handleChange}
                placeholder="Orn: Frontend Developer"
              />
            </div>
            <div>
              <Label>Sektör</Label>
              <Input
                name="sector"
                value={form.sector}
                onChange={handleChange}
                placeholder="Orn: FinTech"
              />
            </div>
            <div>
              <Label>Seviye</Label>
              <Input
                name="level"
                value={form.level}
                onChange={handleChange}
                placeholder="junior / mid / senior"
              />
            </div>
            <div>
              <Label>Calışma Tercihi</Label>
              <Input
                name="employment"
                value={form.employment}
                onChange={handleChange}
                placeholder="full-time / intern / freelance"
              />
            </div>
          </Grid>
          <SectionTitle>Kısa Özet</SectionTitle>
          <TextArea
            name="summary"
            value={form.summary}
            onChange={handleChange}
            placeholder="Kısa profesyonel özet"
          />

          <SectionTitle>Beceriler (virgulle)</SectionTitle>
          <Input
            name="skills"
            value={form.skills}
            onChange={handleChange}
            placeholder="React, TypeScript, Node.js"
          />

          <SectionTitle>Eğitim (virgulle)</SectionTitle>
          <Input
            name="education"
            value={form.education}
            onChange={handleChange}
            placeholder="Universite 1, Universite 2"
          />

          <SectionTitle>Sertifikalar (virgulle)</SectionTitle>
          <Input
            name="certifications"
            value={form.certifications}
            onChange={handleChange}
            placeholder="AWS CCP, Google Analytics"
          />

          <SectionTitle>Projeler (virgulle)</SectionTitle>
          <Input
            name="projects"
            value={form.projects}
            onChange={handleChange}
            placeholder="Blog platformu, Dashboard"
          />

          <SectionTitle>Linkedin URL</SectionTitle>
          <Input
            name="linkedin"
            value={form.linkedin}
            onChange={handleChange}
            placeholder="https://linkedin.com/in/..."
          />

          <div style={{ marginTop: '1rem' }}>
            <Button onClick={advise} disabled={loading}>
              {loading ? <Loader size={18} className="animate-spin" /> : <Brain size={18} />}
              {loading ? 'Oluşturuluyor...' : 'Tavsiyeleri Al'}
            </Button>
          </div>
        </Card>

        {result && (
          <>
            <Card>
              <SectionTitle>Öncelikli Öneriler</SectionTitle>
              <List>
                {(result.priorities || []).map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </List>
            </Card>

            {result.linkedinSuggestion && (
              <Card>
                <SectionTitle>Linkedin Önerisi</SectionTitle>
                <p>
                  <strong>Başlık:</strong> {result.linkedinSuggestion.headline}{' '}
                  <button onClick={() => copy(result.linkedinSuggestion.headline)}>
                    <Copy size={14} />
                  </button>
                </p>
                <p>
                  <strong>Hakkında:</strong> {result.linkedinSuggestion.about}{' '}
                  <button onClick={() => copy(result.linkedinSuggestion.about)}>
                    <Copy size={14} />
                  </button>
                </p>
              </Card>
            )}

            {result.coverPhrases && (
              <Card>
                <SectionTitle>Ön Yazı Cümleleri</SectionTitle>
                <List>
                  {result.coverPhrases.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </List>
              </Card>
            )}

            {result.highlightsForInterview && (
              <Card>
                <SectionTitle>Mülakat Vurgu Noktaları</SectionTitle>
                <List>
                  {result.highlightsForInterview.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </List>
              </Card>
            )}

            {result.roadmap && (
              <Card>
                <SectionTitle>Yol Haritasi</SectionTitle>
                <Grid>
                  <div>
                    <h4>3 Ay</h4>
                    <List>
                      {(result.roadmap.month_3 || []).map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </List>
                  </div>
                  <div>
                    <h4>6 Ay</h4>
                    <List>
                      {(result.roadmap.month_6 || []).map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </List>
                  </div>
                  <div>
                    <h4>12 Ay</h4>
                    <List>
                      {(result.roadmap.month_12 || []).map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </List>
                  </div>
                </Grid>
              </Card>
            )}

            {result.courses && (
              <Card>
                <SectionTitle>Kurs/Sertifika Önerileri</SectionTitle>
                <List>
                  {result.courses.map((c, i) => (
                    <li key={i}>
                      <strong>{c.title}</strong> - {c.type}{' '}
                      {c.note ? `(${c.note})` : ''}
                    </li>
                  ))}
                </List>
              </Card>
            )}

            {result.weeklyChecklist && (
              <Card>
                <SectionTitle>Haftalık Yapılacaklar</SectionTitle>
                <List>
                  {result.weeklyChecklist.map((t, i) => (
                    <li key={i}>
                      <CheckCircle size={14} style={{ marginRight: 6 }} />
                      {t.item}
                    </li>
                  ))}
                </List>
              </Card>
            )}
          </>
        )}
      </Container>
      <AccessGateModal type={gate} onClose={closeGate} />
    </>
  );
};

export default CareerCoach;


